import React from 'react'
import * as R from 'ramda'
import styled from 'styled-components'
import { PropTypes } from 'prop-types'
import { Modal, Button } from 'semantic-ui-react'

import Table from 'shared/components/Table'
import Popup from 'shared/components/Popup'
import { getAntdColumnMaxWidth } from 'libs/table/utils'
import { getDomainStatus } from 'organisms/EmailSetting/utils'
import { Condition } from 'components/Condition'
import { Loader } from 'shared/components/Loader'
import constants from 'config/constants'
import { AllowPermission } from 'shared/components/AllowPermission'
import OptionsDropdown from 'components/OptionsDropdown'

const WarningBox = styled.div`
  background: #fffaf3;
  color: #573a08;
  padding: 1em;

  i {
    margin-right: 10px;
  }

  p {
    margin-top: 0.6em;
    margin-bottom: 0em;
  }

  .text-orange {
    margin-top: 0;
  }
`

const LoaderWrapper = styled.div`
  min-height: 200px;
  position: relative;
`

const DnsTableWrapper = styled.div`
  margin-bottom: 1em;

  .ant-table-row.positive > .ant-table-cell,
  .ant-table-row.positive:hover > .ant-table-cell {
    background: #fcfff5;
    color: #2c662d;
  }

  .ant-table-row.negative > .ant-table-cell,
  .ant-table-row.negative:hover > .ant-table-cell {
    background: #fff6f6;
    color: #9f3a38;
  }

  .ant-table-row.warning > .ant-table-cell,
  .ant-table-row.warning:hover > .ant-table-cell {
    background: #fffaf3;
    color: #573a08;
  }
`

const STATUS_ICON_BY_STATE = {
  validated: <i className="fas fa-check icon text-green" />,
  failed: <i className="fas fa-xmark icon" />,
  pending: <i className="far fa-exclamation-triangle icon text-orange" />,
}

const STATUS_ROW_CLASSNAME_BY_STATE = {
  validated: 'positive',
  failed: 'negative',
  pending: 'warning',
}

const dnsColumns = [
  {
    title: 'Status',
    dataIndex: '_status',
    key: 'status',
    width: 80,
    render: (status) => STATUS_ICON_BY_STATE[status],
  },
  {
    title: 'Type',
    dataIndex: 'type',
    key: 'type',
    width: 100,
    render: (type) => <span style={{ textTransform: 'uppercase' }}>{type}</span>,
  },
  {
    title: 'Name',
    dataIndex: 'host',
    key: 'host',
    ...getAntdColumnMaxWidth(400),
    render: (host) => <Popup.Truncated content={host} />,
  },
  {
    title: 'Value',
    dataIndex: 'data',
    key: 'data',
    ...getAntdColumnMaxWidth(300),
    render: (data) => <Popup.Truncated content={data} />,
  },
]

const buildDnsDataSource = (domain) =>
  R.keys(domain.dns).map((key) => ({
    key,
    _status: getDomainStatus(domain.dns[key].valid, domain.last_validation_attempt_at),
    type: domain.dns[key].type,
    host: domain.dns[key].host,
    data: domain.dns[key].data,
  }))

export const DnsRecordModalPresentation = ({
  isModalOpen,
  closeModal,
  domain,
  loading,
  handleValidataion,
  warningResult,
  onDeleteClick,
}) => (
  <Modal className="coupled second large modal" open={isModalOpen} onClose={closeModal}>
    <Modal.Header>
      <div className="flex items-baseline justify-between gap-4">
        <div className="flex-1 min-w-0 truncate">DNS Records for {domain.domain}</div>
        <div className="shrink-0">
          <AllowPermission permission={constants.permissions.manageEmailAuthentication}>
            <OptionsDropdown
              options={[
                {
                  text: 'Delete',
                  icon: 'far fa-trash-can',
                  onClick: onDeleteClick,
                  className: '!text-negative',
                },
              ]}
            />
          </AllowPermission>
        </div>
      </div>
    </Modal.Header>
    <Modal.Content>
      <p>You will need to install the following records to complete the process.</p>
      <p>
        <a
          href="https://help.simplesat.io/en/articles/2207534-email-domains-deliverability#authenticate-your-sending-domain"
          target="_blank"
          rel="noreferrer"
        >
          <i className="icon far fa-book" />
          How to set up domain authentication
        </a>
      </p>
      <Condition condition={loading}>
        <Condition.True>
          <LoaderWrapper>
            <Loader />
          </LoaderWrapper>
        </Condition.True>
        <Condition.False>
          <React.Fragment>
            <Condition.True condition={!R.isEmpty(warningResult) && !domain.valid}>
              <WarningBox className="warning">
                <p className="text-orange">
                  <i className="far fa-exclamation-triangle icon text-orange" />
                  We received the following errors when validating the record.
                </p>
                {R.keys(warningResult).map((key) => (
                  <Condition.False key={key} condition={warningResult[key].valid}>
                    <p> {warningResult[key].reason}</p>
                  </Condition.False>
                ))}
              </WarningBox>
            </Condition.True>
            <DnsTableWrapper>
              <Table
                variant="indexPage"
                clickable={false}
                columns={dnsColumns}
                dataSource={buildDnsDataSource(domain)}
                rowClassName={(record) => STATUS_ROW_CLASSNAME_BY_STATE[record._status]}
              />
            </DnsTableWrapper>
          </React.Fragment>
        </Condition.False>
      </Condition>
      <AllowPermission permission={constants.permissions.manageEmailAuthentication}>
        <Button primary onClick={handleValidataion} disabled={loading}>
          <i className="fas fa-check icon" />
          Validate DNS Records
        </Button>
      </AllowPermission>
    </Modal.Content>
    <Modal.Actions>
      <Button onClick={closeModal}>Cancel</Button>
    </Modal.Actions>
  </Modal>
)

DnsRecordModalPresentation.propTypes = {
  isModalOpen: PropTypes.bool,
  closeModal: PropTypes.func,
  domain: PropTypes.object,
  loading: PropTypes.bool,
  handleValidataion: PropTypes.func,
  warningResult: PropTypes.object,
  onDeleteClick: PropTypes.func,
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/organisms/DnsRecordModal/index.js